import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { Therapist } from "../../../therapist";
import { updateTherapistProfile } from "../../application/therapistProfile";
import { useTherapist } from "../../../../context/useTherapist";
import Button from "../../../../shared/ui/button/Button";
import ComponentCard from "../../../../shared/ui/common/ComponentCard";

interface PatientTermCardProps {
    therapist: Therapist | null;
}

/**
 * Lets the therapist choose how the app labels the people they treat
 * ("patient" / "client" / "consultee"). Applied app-wide via the i18n
 * post-processor. Its own labels disable post-processing so the options always
 * show their real names.
 */
export default function PatientTermCard({ therapist }: PatientTermCardProps) {
    const { t } = useTranslation();
    const { setTherapist } = useTherapist();
    const [patientTerm, setPatientTermValue] = useState(therapist?.patient_term ?? "patient");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setPatientTermValue(therapist?.patient_term ?? "patient");
    }, [therapist]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!therapist) return;
        try {
            setLoading(true);
            const updated = await updateTherapistProfile(therapist.id, {
                ...therapist,
                patient_term: patientTerm,
            });
            setTherapist(updated);
            alert(t("common.success"));
        } catch (err) {
            console.error("Error updating patient term", err);
            alert(t("common.error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <ComponentCard
            glass
            title={
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                        <span className="block text-lg font-bold text-content-primary truncate">
                            {t("profile.patientTerm.title")}
                        </span>
                        <p className="hidden sm:block text-xs text-content-tertiary font-normal">
                            {t("profile.patientTerm.label")}
                        </p>
                    </div>
                </div>
            }
        >
            <form onSubmit={handleSave} className="space-y-6">
                <div className="max-w-md group">
                    <p className="sm:hidden text-sm text-content-tertiary mb-4 px-1">
                        {t("profile.patientTerm.label")}
                    </p>
                    <select
                        className="w-full h-12 rounded-2xl border border-gray-100 bg-white/50 px-4 py-2 text-sm text-gray-900 focus:border-accent dark:bg-white/[0.03] dark:border-white/5 dark:text-white transition-all shadow-theme-xs group-hover:border-gray-200 dark:group-hover:border-white/10"
                        value={patientTerm}
                        onChange={(e) => setPatientTermValue(e.target.value as Therapist["patient_term"])}
                    >
                        {/* postProcess disabled so the options keep their real names. */}
                        <option value="patient">{t("profile.patientTerm.patient")}</option>
                        <option value="client">{t("profile.patientTerm.client")}</option>
                        <option value="consultee">{t("profile.patientTerm.consultee")}</option>
                    </select>
                    <p className="mt-2 px-1 text-xs text-content-tertiary">
                        {t("profile.patientTerm.hint")}
                    </p>
                </div>

                <div className="flex justify-start">
                    <Button
                        type="submit"
                        disabled={loading}
                        className="shadow-lg shadow-brand-500/20 px-8 py-3 font-bold"
                    >
                        {loading ? (
                            <div className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {t("common.saving")}
                            </div>
                        ) : t("profile.sessionConfig.save")}
                    </Button>
                </div>
            </form>
        </ComponentCard>
    );
}
